import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, Filter, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getAccessibleClients, type AccessibleClient } from "@/services/clientsService";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";

interface StatsFilterProps {
  onFilterChange?: (filters: {
    startDate: Date | undefined;
    endDate: Date | undefined;
    clientes: string[];
  }) => void;
}

export const StatsFilter = ({ onFilterChange }: StatsFilterProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedClientes, setSelectedClientes] = useState<string[]>(["todos"]);
  const [clientesPopoverOpen, setClientesPopoverOpen] = useState(false);
  const [availableClients, setAvailableClients] = useState<AccessibleClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonthsByYear, setAvailableMonthsByYear] = useState<Record<number, number[]>>({});
  const [clientSearchValue, setClientSearchValue] = useState("");
  const { toast } = useToast();
  const { isAdminOrSupport } = usePermissions();
  const showClientFilter = isAdminOrSupport;
  const didAutoApplyForClient = useRef(false);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    loadClients();
    loadAvailablePeriods();
  }, []);

  useEffect(() => {
    if (isAdminOrSupport) {
      const validClientes = selectedClientes.filter((clienteValue) => {
        if (clienteValue === "todos" || clienteValue === "sandbox") {
          return true;
        }
        return availableClients.some((c) => c.id === clienteValue);
      });

      if (validClientes.length !== selectedClientes.length) {
        setSelectedClientes(validClientes.length > 0 ? validClientes : ["todos"]);
      }
      return;
    }

    if (loadingClients) {
      return;
    }

    const lockedIds = availableClients.map((c) => c.id);
    const same =
      lockedIds.length === selectedClientes.length &&
      lockedIds.every((id) => selectedClientes.includes(id));

    if (!same) {
      setSelectedClientes(lockedIds);
    }

    if (!didAutoApplyForClient.current && lockedIds.length > 0) {
      didAutoApplyForClient.current = true;
      const startDate = new Date(selectedYear, selectedMonth - 1, 1);
      const endDate = new Date(selectedYear, selectedMonth, 0);
      onFilterChangeRef.current?.({
        startDate,
        endDate,
        clientes: lockedIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableClients, isAdminOrSupport, loadingClients]);

  useEffect(() => {
    loadAvailablePeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientes.join(",")]);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await getAccessibleClients();

      if (response.success && response.data) {
        setAvailableClients(response.data.clients);
      } else {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao carregar clientes",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro",
        description: "Erro ao carregar clientes",
        variant: "destructive",
      });
    } finally {
      setLoadingClients(false);
    }
  };

  const loadAvailablePeriods = async () => {
    try {
      const { getAvailablePeriods } = await import("@/services/apiCallsService");

      let clientId: string | null | undefined = undefined;
      if (selectedClientes.length > 0 && !selectedClientes.includes("todos")) {
        if (selectedClientes.includes("sandbox")) {
          clientId = "null";
        } else if (selectedClientes.length === 1) {
          clientId = selectedClientes[0];
        }
      }

      const response = await getAvailablePeriods({ client_id: clientId });

      if (response.success && response.data) {
        setAvailableYears(response.data.years);
        setAvailableMonthsByYear(response.data.months_by_year);

        if (response.data.years.length > 0) {
          if (!response.data.years.includes(selectedYear)) {
            setSelectedYear(response.data.years[0]);
          }

          const monthsForYear = response.data.months_by_year[selectedYear] || [];
          if (monthsForYear.length > 0 && !monthsForYear.includes(selectedMonth)) {
            setSelectedMonth(monthsForYear[0]);
          }
        }
      }
    } catch {
    }
  };

  const meses = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const anos = availableYears.length > 0
    ? availableYears
    : Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const clientes = [
    ...(showClientFilter ? [{ value: "todos", label: "Todos os Clientes" }] : []),
    ...(showClientFilter ? [{ value: "sandbox", label: "Sandbox" }] : []),
    ...availableClients.map((client) => ({
      value: client.id,
      label: client.company,
    })),
  ];

  const filteredClientes = clientSearchValue
    ? clientes.filter((cliente) =>
        cliente.label.toLowerCase().includes(clientSearchValue.toLowerCase()),
      )
    : clientes;

  const resolveFinalClientes = () => {
    if (!showClientFilter) {
      return availableClients.map((c) => c.id);
    }

    const validClientes = selectedClientes.filter((clienteValue) => {
      if (clienteValue === "todos") {
        return true;
      }
      if (clienteValue === "sandbox") {
        return isAdminOrSupport;
      }
      return availableClients.some((c) => c.id === clienteValue);
    });

    return validClientes.length > 0 ? validClientes : ["todos"];
  };

  const handleApplyFilter = () => {
    const finalClientes = resolveFinalClientes();
    const startDate = new Date(selectedYear, selectedMonth - 1, 1);
    const endDate = new Date(selectedYear, selectedMonth, 0);

    onFilterChange?.({
      startDate,
      endDate,
      clientes: finalClientes,
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClienteToggle = (clienteValue: string) => {
    if (!showClientFilter) {
      return;
    }

    if (clienteValue === "todos") {
      setSelectedClientes(["todos"]);
    } else if (clienteValue === "sandbox") {
      if (!isAdminOrSupport) {
        return;
      }
      setSelectedClientes(["sandbox"]);
    } else {
      const isAccessibleClient = availableClients.some((c) => c.id === clienteValue);
      if (!isAccessibleClient) {
        return;
      }

      setSelectedClientes((prev) => {
        const newSelection = prev.filter((c) => c !== "todos" && c !== "sandbox");
        if (newSelection.includes(clienteValue)) {
          const filtered = newSelection.filter((c) => c !== clienteValue);
          return filtered.length === 0 ? ["todos"] : filtered;
        }
        return [...newSelection, clienteValue];
      });
    }
  };

  const removeCliente = (clienteValue: string) => {
    if (!showClientFilter) {
      return;
    }
    setSelectedClientes((prev) => {
      const filtered = prev.filter((c) => c !== clienteValue);
      return filtered.length === 0 ? ["todos"] : filtered;
    });
  };

  const getClientSummaryLabel = () => {
    if (!showClientFilter) {
      return availableClients
        .map((c) => c.company)
        .filter(Boolean)
        .join(", ");
    }

    if (selectedClientes.includes("todos")) {
      return "Todos os clientes";
    }
    if (selectedClientes.includes("sandbox")) {
      return "Sandbox";
    }
    return selectedClientes
      .map((value) => clientes.find((c) => c.value === value)?.label)
      .filter(Boolean)
      .join(", ");
  };

  const getFilterSummary = () => {
    const parts = [];
    const mesLabel = meses.find((m) => m.value === selectedMonth)?.label;
    parts.push(`${mesLabel} ${selectedYear}`);
    const clientLabel = getClientSummaryLabel();
    if (clientLabel) {
      parts.push(clientLabel);
    }
    return parts.length > 0 ? ` - ${parts.join(", ")}` : "";
  };

  const getSelectedClientesDisplay = () => {
    if (selectedClientes.includes("todos")) {
      return "Todos os Clientes";
    }
    if (selectedClientes.includes("sandbox")) {
      return "Sandbox";
    }
    if (selectedClientes.length === 1) {
      return clientes.find((c) => c.value === selectedClientes[0])?.label;
    }
    return `${selectedClientes.length} clientes selecionados`;
  };

  const filterTitle = showClientFilter
    ? "Filtros de Período e Cliente"
    : "Filtros de Período";

  return (
    <Card className="mb-6 border-muted/40">
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={toggleExpanded}>
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-medium">
            {filterTitle}
            {!isExpanded && getFilterSummary()}
          </h3>
        </div>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          <div
            className={cn(
              "grid gap-4",
              showClientFilter ? "md:grid-cols-3" : "md:grid-cols-2",
            )}
          >
            <div className="space-y-2">
              <label className="text-sm font-medium">Mês</label>
              <Select
                value={selectedMonth.toString()}
                onValueChange={(value) => setSelectedMonth(parseInt(value))}
                disabled={anos.length === 0}
              >
                <SelectTrigger className="w-full bg-card">
                  <SelectValue placeholder={anos.length === 0 ? "Selecione um ano primeiro" : "Selecionar mês"} />
                </SelectTrigger>
                <SelectContent className="bg-card border z-50">
                  {(() => {
                    const monthsForYear = availableMonthsByYear[selectedYear] || [];
                    const availableMonths = meses.filter((m) => monthsForYear.includes(m.value));

                    if (availableMonths.length === 0) {
                      return <SelectItem value="__disabled__" disabled>Nenhum mês disponível para este ano</SelectItem>;
                    }

                    return availableMonths.map((mes) => (
                      <SelectItem key={mes.value} value={mes.value.toString()}>
                        {mes.label}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Ano</label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => {
                  const newYear = parseInt(value);
                  setSelectedYear(newYear);
                  const monthsForYear = availableMonthsByYear[newYear] || [];
                  if (monthsForYear.length > 0 && !monthsForYear.includes(selectedMonth)) {
                    setSelectedMonth(monthsForYear[0]);
                  }
                }}
                disabled={anos.length === 0}
              >
                <SelectTrigger className="w-full bg-card">
                  <SelectValue placeholder={anos.length === 0 ? "Nenhum ano disponível" : "Selecionar ano"} />
                </SelectTrigger>
                <SelectContent className="bg-card border z-50">
                  {anos.length === 0 ? (
                    <SelectItem value="__disabled__" disabled>Nenhum ano disponível</SelectItem>
                  ) : (
                    anos.map((ano) => (
                      <SelectItem key={ano} value={ano.toString()}>
                        {ano}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {showClientFilter && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Clientes</label>
                <Popover
                  open={clientesPopoverOpen}
                  onOpenChange={(open) => {
                    setClientesPopoverOpen(open);
                    if (!open) {
                      setClientSearchValue("");
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientesPopoverOpen}
                      className="w-full justify-between bg-card"
                    >
                      {getSelectedClientesDisplay()}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-card border z-50">
                    <Command>
                      <CommandInput
                        placeholder="Buscar cliente..."
                        value={clientSearchValue}
                        onChange={(e) => setClientSearchValue(e.target.value)}
                      />
                      <CommandList>
                        {loadingClients ? (
                          <div className="p-4 text-sm text-muted-foreground">Carregando clientes...</div>
                        ) : filteredClientes.length === 0 ? (
                          <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                        ) : (
                          <CommandGroup>
                            {filteredClientes.map((cliente) => (
                              <CommandItem
                                key={cliente.value}
                                value={cliente.value}
                                onSelect={() => handleClienteToggle(cliente.value)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedClientes.includes(cliente.value)
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                {cliente.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          {showClientFilter &&
            !selectedClientes.includes("todos") &&
            selectedClientes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedClientes.map((clienteValue) => {
                  const cliente = clientes.find((c) => c.value === clienteValue);
                  return (
                    <Badge key={clienteValue} variant="secondary" className="text-xs">
                      {cliente?.label}
                      <button
                        onClick={() => removeCliente(clienteValue)}
                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleApplyFilter} className="px-8">
              Aplicar Filtros
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
            <span className="text-xs text-muted-foreground">Filtros atuais:</span>
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
              {meses.find((m) => m.value === selectedMonth)?.label} {selectedYear}
            </span>
            {!showClientFilter ? (
              availableClients.map((client) => (
                <span
                  key={client.id}
                  className="px-2 py-1 bg-primary/10 text-primary text-xs rounded"
                >
                  {client.company}
                </span>
              ))
            ) : selectedClientes.includes("todos") ? (
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                Todos os clientes
              </span>
            ) : selectedClientes.includes("sandbox") ? (
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                Sandbox
              </span>
            ) : (
              selectedClientes.map((clienteValue) => {
                const cliente = clientes.find((c) => c.value === clienteValue);
                return (
                  <span
                    key={clienteValue}
                    className="px-2 py-1 bg-primary/10 text-primary text-xs rounded"
                  >
                    {cliente?.label}
                  </span>
                );
              })
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
